import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { usePractitioners } from 'api/queries/marketing-capabilities/practitioners/usePractitioners'
import { useResources } from 'api/queries/marketing-capabilities/resources/useResources'
import { useSkills } from 'api/queries/marketing-capabilities/skills/useSkills'
import { useExploreLearnings } from 'store/ExploreLearnings'

import { HeaderPage } from '../constants/header.constants'

const getFallbackOrder = (current: HeaderPage): HeaderPage[] => {
  if (current === 'skills') return ['resources', 'community']
  if (current === 'resources') return ['community', 'skills']
  return ['skills', 'resources']
}

interface UseSearchNavigationOptions {
  redirectFromSkillPage?: boolean
}

export const useSearchNavigation = (currentPage: HeaderPage, options?: UseSearchNavigationOptions) => {
  const navigate = useNavigate()
  const location = useLocation()
  const search = useExploreLearnings(state => state.search)

  const hasNavigatedRef = useRef(false)
  const searchRef = useRef('')

  const [activeTabs, setActiveTabs] = useState<HeaderPage[]>([])

  useEffect(() => {
    if (!search) {
      setActiveTabs([])
      hasNavigatedRef.current = false
      searchRef.current = ''
      return
    }

    if (searchRef.current !== search) {
      searchRef.current = search
      hasNavigatedRef.current = false
      setActiveTabs([currentPage])
    }
  }, [search, currentPage])

  const { data: skillsData, isLoading: skillsLoading } = useSkills()
  const { data: resourcesData, isLoading: resourcesLoading } = useResources()
  const { data: communityData, isLoading: communityLoading } = usePractitioners()

  useEffect(() => {
    if (!search || !options?.redirectFromSkillPage) return
    if (location.pathname.startsWith('/explore-learnings/skill/')) {
      navigate(`/explore-learnings/${currentPage}`, {
        state: { from: '/skill' },
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, options?.redirectFromSkillPage, location.pathname, currentPage])

  const getCount = (tab: HeaderPage) => {
    if (tab === 'skills') {
      return skillsData?.meta?.total ?? skillsData?.skills?.length ?? 0
    }
    if (tab === 'resources') {
      return resourcesData?.meta?.total ?? 0
    }
    return communityData?.meta?.total ?? 0
  }

  const loadingMap = {
    skills: skillsLoading,
    resources: resourcesLoading,
    community: communityLoading,
  }

  useEffect(() => {
    if (!search || hasNavigatedRef.current) return

    if (!activeTabs.includes(currentPage)) return

    if (loadingMap[currentPage]) return

    const currentCount = getCount(currentPage)

    if (currentCount > 0) {
      hasNavigatedRef.current = true
      return
    }

    const fallbackOrder = getFallbackOrder(currentPage)

    const nextTab = fallbackOrder.find(tab => !activeTabs.includes(tab))

    if (nextTab) {
      setActiveTabs(prev => (prev.includes(nextTab) ? prev : [...prev, nextTab]))
      return
    }

    for (let tab of fallbackOrder) {
      if (loadingMap[tab]) return

      const count = getCount(tab)

      if (count > 0) {
        hasNavigatedRef.current = true
        navigate(`/explore-learnings/${tab}`, {
          state: location.state,
        })
        return
      }
    }

    hasNavigatedRef.current = true
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    search,
    currentPage,
    activeTabs,
    skillsData,
    resourcesData,
    communityData,
    skillsLoading,
    resourcesLoading,
    communityLoading,
  ])
}
